import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Slider, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  handleCloseFullResearchDialog: () => void;
  handleSubmitFullResearch: () => void;
  isProcessingFull: boolean;
  editableProduct: string;
  editableRegion: string;
  editablePrompt: string;
  regionSplitInstructions: string;
  subsearchInstructions: string;
  numRegions: number;
  numSubsearches: number;
  peopleSearchInstructions: string;
  numPeopleSearches: number;

  setEditableProduct: (product: string) => void;
  setEditableRegion: (region: string) => void;
  setEditablePrompt: (prompt: string) => void;
  setNumRegions: (numRegions: number) => void;
  setNumSubsearches: (numSubsearches: number) => void;
  setPeopleSearchInstructions: (instructions: string) => void;
  setNumPeopleSearches: (numPeopleSearches: number) => void;
  setRefineRegionSplitInstructions: (instructions: string) => void;
  setRefineSubsearchInstructions: (instructions: string) => void;
  onProcessFullDirect: () => void;
  isProcessingFullDirect: boolean;
}

const FullResearchDialog: React.FC<Props> = ({
  open,
  handleCloseFullResearchDialog,
  handleSubmitFullResearch,
  isProcessingFull,
  editableProduct,
  editableRegion,
  editablePrompt,
  regionSplitInstructions,
  numRegions,
  subsearchInstructions,
  numSubsearches,
  peopleSearchInstructions,
  numPeopleSearches,
  setEditableProduct,
  setEditableRegion,
  setEditablePrompt,
  setNumRegions,
  setNumSubsearches,
  setPeopleSearchInstructions,
  setNumPeopleSearches,
  setRefineRegionSplitInstructions,
  setRefineSubsearchInstructions,
  onProcessFullDirect,
  isProcessingFullDirect,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth transitionDuration={0} maxWidth={'md'}>
      <DialogTitle className={classes.title}>
        <Box display="flex" alignItems="center" gap={1}>
          <RocketLaunchIcon sx={{ color: '#FFF' }} />
          <Typography className={classes.titleText}>{t('generateFullLeadList')}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <Alert className={classes.alert} severity="info">
            {t('fullLeadInfoAlert')}
          </Alert>
          <Alert className={classes.alert} severity="success">
            <Typography className={classes.label} fontWeight="bold" gutterBottom>
              {t('implementationNotes')}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              <li>{t('regionSplitting')}</li>
              <li>{t('structuredLeads')}</li>
              <li>{t('qualityAssessed')}</li>
              <li>{t('durationNote')}</li>
              <li>{t('usePreviousResults')}</li>
              <li>{t('updateInsteadOfNew')}</li>
            </ul>
          </Alert>

          <Divider sx={{ my: 1 }} />

          <Typography className={classes.sectionTitle}>{t('researchParameters')}</Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <PaiTextField
                label={t('product')}
                value={editableProduct}
                onChange={(e) => setEditableProduct(e.target.value)}
                placeholder={t('productPlaceholder')}
                variant={'white'}
                disabled={isProcessingFull}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <PaiTextField
                label={t('region')}
                value={editableRegion}
                onChange={(e) => setEditableRegion(e.target.value)}
                placeholder={t('regionPlaceholder')}
                variant={'white'}
                disabled={isProcessingFull}
              />
            </Grid>
            <Grid size={12}>
              <PaiTextArea
                label={t('searchPrompt')}
                value={editablePrompt}
                onChange={(e) => setEditablePrompt(e.target.value)}
                placeholder={t('searchPromptPlaceholder')}
                variant={'white'}
                disabled={isProcessingFull}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />
          <Typography className={classes.sectionTitle}>{t('advancedSettings')}</Typography>

          <Stack gap={3}>
            <PaiTextArea
              label={t('regionSplitInstruction')}
              value={regionSplitInstructions}
              onChange={(e) => setRefineRegionSplitInstructions(e.target.value)}
              placeholder={t('regionSplitPlaceholder')}
              variant={'white'}
              disabled={isProcessingFull}
            />
            <Stack gap={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography className={classes.label}>{t('numSubregions', { count: numRegions })}</Typography>
                <Typography className={classes.caption}>{t('subregionInfo')}</Typography>
              </Stack>
              <Slider
                value={numRegions}
                onChange={(_, newValue) => setNumRegions(newValue as number)}
                min={2}
                max={20}
                step={1}
                marks={[
                  { value: 2, label: '2' },
                  { value: 6, label: '6' },
                  { value: 10, label: '10' },
                  { value: 15, label: '15' },
                  { value: 20, label: '20' },
                ]}
                disabled={isProcessingFull}
              />
            </Stack>
          </Stack>

          <Divider sx={{ my: 1 }} />
          <Typography className={classes.sectionTitle}>{t('companyInstructions')}</Typography>
          <Typography className={classes.description}>{t('companyInstructionsDescription')}</Typography>

          <Stack gap={3}>
            <PaiTextArea
              label={t('subsearchInstructions')}
              value={subsearchInstructions}
              onChange={(e) => setRefineSubsearchInstructions(e.target.value)}
              placeholder={t('subsearchPlaceholder')}
              variant={'white'}
              disabled={isProcessingFull}
            />
            <Stack gap={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography className={classes.label}>{t('numSubsearches', { count: numSubsearches })}</Typography>
                <Typography className={classes.caption}>{t('subsearchInfo')}</Typography>
              </Stack>
              <Slider
                value={numSubsearches}
                onChange={(_, newValue) => setNumSubsearches(newValue as number)}
                min={2}
                max={15}
                step={1}
                marks={[
                  { value: 2, label: '2' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                  { value: 15, label: '15' },
                ]}
                disabled={isProcessingFull}
              />
            </Stack>
          </Stack>

          <Divider sx={{ my: 1 }} />
          <Typography className={classes.sectionTitle}>{t('peopleSearchInstruction')}</Typography>

          <Stack gap={3}>
            <PaiTextField
              value={peopleSearchInstructions}
              onChange={(e) => setPeopleSearchInstructions(e.target.value)}
              placeholder={t('peopleSearchPlaceholder')}
              variant={'white'}
              disabled={isProcessingFull}
            />
            <Stack gap={1}>
              <Typography className={classes.label}>{t('peopleSearchCount')}</Typography>
              <Slider
                value={numPeopleSearches}
                onChange={(_, value) => setNumPeopleSearches(value as number)}
                min={1}
                max={10}
                step={1}
                valueLabelDisplay="auto"
                marks={[
                  { value: 1, label: '1' },
                  { value: 3, label: '3' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                ]}
                disabled={isProcessingFull}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline="always" action={handleCloseFullResearchDialog} />
        <PaiButton
          text={isProcessingFullDirect ? t('processingFullDirect') : t('processFullDirect')}
          action={onProcessFullDirect}
          size="small"
          border="rounded"
          loading={isProcessingFullDirect}
          disabled={isProcessingFullDirect}
        />
        <PaiButton
          text={t('generateLeadList')}
          action={handleSubmitFullResearch}
          size="small"
          border="rounded"
          loading={isProcessingFull}
          disabled={isProcessingFull}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  label: {
    fontSize: theme.typography.pxToRem(14),
  },
  caption: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  description: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  alert: {
    margin: '0 !important',
    borderRadius: '8px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default FullResearchDialog;
